'use strict';

// Just a generic string to hash generator, nothing special
const generateDialogId = url => {
    let hash = 0;

    for (let i = 0; i < url.length; i++) {
        hash = ((hash << 5) - hash + url.charCodeAt(i)) | 0;
    }

    return `video-modal-${(hash >>> 0).toString(36)}`;
}

// This will build our dialog element - it's pretty minimal
// Markup generated is:
// <dialog id="<!-- id inserted here -->" class="video-modal" closedby="any" aria-labelledby="<!-- id inserted here -->-title">
//     <button type="button" class="video-modal__close" aria-label="Close video">
//         <svg class="o-icon" aria-hidden="true">
//             <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-close"></use>
//         </svg>
//     </button>
//
//     <h2 id="<!-- id inserted here -->-title" class="sr-only">Video: <span><!-- title inserted here --></span></h2>
//
//     <div class="video-modal__inner">
//         <div class="video-modal__media">
//             <!-- iframe inserted here -->
//         </div>
//     </div>
// </dialog>
const createDialog = (id, data) => {
    const dialog = document.createElement('dialog');
    dialog.id = id;
    dialog.className = 'video-modal';
    dialog.setAttribute('closedby', 'any');
    dialog.setAttribute('aria-labelledby', `${id}-title`);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'video-modal__close';
    button.innerHTML = `
        <svg class="o-icon" aria-hidden="true">
            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-close"></use>
        </svg>
    `;
    button.setAttribute('aria-label', 'Close video');
    dialog.appendChild(button);

    const h2 = document.createElement('h2');
    h2.id = `${id}-title`;
    h2.className = 'sr-only';
    h2.appendChild(document.createTextNode('Video: '));

    const span = document.createElement('span');
    span.innerHTML = data.title;

    h2.appendChild(span);

    dialog.appendChild(h2);

    const inner = document.createElement('div');
    inner.className = 'video-modal__inner';

    const media = document.createElement('div');
    media.className = 'video-modal__media';
    media.innerHTML = data.html;

    inner.appendChild(media);
    dialog.appendChild(inner);

    return dialog;
}

// Grabs the host from the iframe src
const determineIframeOrigin = iframe => {
    const a = document.createElement('a');
    a.href = iframe.src;
    return a.protocol + '//' + a.hostname + (a.port ? ':' + a.port : '');
}

// Sends play signals to the respective players in the iframe
const playVideo = (iframe, isVimeo) => {
    // Origin has to be set so iframe knows the message is from us
    const origin = determineIframeOrigin(iframe);

    if (isVimeo) {
        iframe.contentWindow.postMessage({ method: 'play' }, origin);
    } else {
        iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), origin
        );
    }
}

// Sends stop* signals to the respective players in the iframe
// *Vimeo doesn't have stop, so we pause and move to the start of the video
// with a small delay to prevent race conditions
const stopVideo = (iframe, isVimeo) => {
    // Origin has to be set so iframe knows the message is from us
    const origin = determineIframeOrigin(iframe);

    if (isVimeo) {
        iframe.contentWindow.postMessage({ method: 'pause' }, origin);

        setTimeout(() => {
            iframe.contentWindow.postMessage({ method: 'setCurrentTime', value: 0 }, origin);
        }, 50);
    } else {
        iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'stopVideo', args: [] }), origin
        );
    }
}

// Sets up the necessary listeners to determine if video can be playable after
// iframe has been added to the DOM
const onVideoReady = (iframe, isVimeo) => {
    // Vimeo specifically posts a ready message, so we start a listener to
    // listen for the ready message, and then remove the listener since it's
    // no longer required, and resolve the promise
    if (isVimeo) {
        return new Promise(resolve => { // eslint-disable-line
            function listener(event) {
                if (event.source !== iframe.contentWindow) return;

                try {
                    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                    if (data.event === 'ready') {
                        window.removeEventListener('message', listener);
                        resolve();
                    }
                } catch (e) {
                    // ignore
                }
            }
            window.addEventListener('message', listener);
        });
    }

    // YouTube _should_ also post a message, but it's not, so we will instead
    // wait for the iframe to load, and assume the video is playable, and
    // resolve the promise
    return new Promise(resolve => { // eslint-disable-line
        iframe.addEventListener('load', () => resolve(), { once: true });
    });
}

(function () {
    for (const trigger of document.querySelectorAll('.js-video-modal')) {
        // If there's no href, there's nothing to do
        if (!trigger.href) {
            continue;
        }

        // Create a unuque ID for our modal
        const id = generateDialogId(trigger.href);

        let dialog;
        let iframe;
        let isVimeo = false;
        let isReadyForPlayback = null;

        const handleClose = () => {
            stopVideo(iframe, isVimeo);
        }

        trigger.addEventListener('click', async function (event) {
            event.preventDefault();

            // Try to grab the dialog element with our unique ID
            dialog = document.getElementById(id);

            // If no dialog is found, we should create one
            if (!dialog) {
                // To do so, we need the information about the video
                let response;

                try {
                    response = await fetch(`/api/oembed?url=${encodeURIComponent(trigger.href)}`).then(response => {  // eslint-disable-line
                        return response.json();
                    });
                } catch (error) {
                    console.error(error);
                }

                // If we don't have a response, forward the user to the href and
                // end execution here
                if (!response) {
                    return (window.location.href = trigger.href);
                }

                // Create a new dialog using response, set unique ID
                dialog = createDialog(id, response); // eslint-disable-line

                // Apply close and cancel listeners that will stop videos
                dialog.addEventListener('close', handleClose);
                dialog.addEventListener('cancel', handleClose);

                // Grab the iframe, and determine if it's Vimeo
                iframe = dialog.querySelector('iframe');
                isVimeo = response.provider_name === 'Vimeo';

                // Grab close button and set it to close the dialog
                const button = dialog.querySelector('button');

                button.addEventListener('click', () => {
                    dialog.close();
                });

                // Set the necessary listener to check if video is playable
                // (or if iframe has loaded in case of YouTube)
                isReadyForPlayback = onVideoReady(iframe, isVimeo);

                // Attach the dialog to body
                document.body.appendChild(dialog);
            }

            // At this point we will have a dialog attached to the body, so
            // show the modal
            dialog.showModal();

            // And finally, play the video as soon as it's playable
            isReadyForPlayback.then(() => {
                playVideo(iframe, isVimeo);
            });
        });
    }
}());
